import { useState } from 'react';
import {
  Shield, CheckCircle2, XCircle, AlertTriangle, Building2, Users,
  Landmark, CalendarClock, Package, Search, TrendingDown, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const fmtPct = (v: number) => `${v.toFixed(1)}%`;

// ==================== Mock Data ====================

interface FundCompliance {
  id: string;
  name: string;
  totalAssets: number;
  inHouseAssetValue: number;
  solePurposeChecks: { criterion: string; passed: boolean; note?: string }[];
}

const FUNDS: FundCompliance[] = [
  {
    id: 'fund-001',
    name: 'Smith Family Super Fund',
    totalAssets: 1_850_000,
    inHouseAssetValue: 45_000,
    solePurposeChecks: [
      { criterion: 'Fund maintained solely for retirement benefits', passed: true },
      { criterion: 'No financial assistance to members or relatives', passed: true },
      { criterion: 'No assets used by members personally', passed: true },
      { criterion: 'Investment strategy documented and followed', passed: true },
      { criterion: 'Arm\'s length transactions with related parties', passed: true },
      { criterion: 'No holiday property used by members', passed: true },
    ],
  },
  {
    id: 'fund-002',
    name: 'Johnson Retirement Fund',
    totalAssets: 2_340_000,
    inHouseAssetValue: 140_000,
    solePurposeChecks: [
      { criterion: 'Fund maintained solely for retirement benefits', passed: true },
      { criterion: 'No financial assistance to members or relatives', passed: true },
      { criterion: 'No assets used by members personally', passed: false, note: 'Holiday property at Gold Coast may be used by trustee Robert Johnson' },
      { criterion: 'Investment strategy documented and followed', passed: true },
      { criterion: 'Arm\'s length transactions with related parties', passed: false, note: 'Lease to related entity below market rate' },
      { criterion: 'No holiday property used by members', passed: false, note: 'Gold Coast apartment flagged - requires review' },
    ],
  },
];

interface RelatedPartyTx {
  date: string;
  description: string;
  parties: string;
  amount: number;
  riskLevel: 'high' | 'medium' | 'low';
  actionRequired: string;
  fund: string;
}

const RELATED_PARTY_TXS: RelatedPartyTx[] = [
  { date: '05/03/2026', description: 'Lease payment - commercial property', parties: 'Johnson Trading Pty Ltd (related entity)', amount: 18_000, riskLevel: 'high', actionRequired: 'Verify market rate valuation', fund: 'Johnson Retirement Fund' },
  { date: '12/02/2026', description: 'Accounting services', parties: 'Smith & Co Accountants (member firm)', amount: 4_500, riskLevel: 'medium', actionRequired: 'Confirm arm\'s length pricing', fund: 'Smith Family Super Fund' },
  { date: '20/01/2026', description: 'Loan repayment from related trust', parties: 'Johnson Family Trust', amount: 50_000, riskLevel: 'high', actionRequired: 'Review LRBA compliance', fund: 'Johnson Retirement Fund' },
  { date: '08/04/2026', description: 'Insurance premium - property', parties: 'ABC Insurance (independent)', amount: 3_200, riskLevel: 'low', actionRequired: 'None - independent party', fund: 'Smith Family Super Fund' },
];

interface LRBA {
  asset: string;
  lender: string;
  loanBalance: number;
  interestRate: number;
  lvr: number;
  safeHarbour: boolean;
  nextRepayment: string;
  fund: string;
}

const LRBAS: LRBA[] = [
  { asset: '42 Collins St, Melbourne VIC 3000', lender: 'National Australia Bank', loanBalance: 380_000, interestRate: 6.25, lvr: 52, safeHarbour: true, nextRepayment: '15/05/2026', fund: 'Smith Family Super Fund' },
  { asset: '15 Surf Parade, Gold Coast QLD 4217', lender: 'Johnson Family Trust', loanBalance: 520_000, interestRate: 4.50, lvr: 65, safeHarbour: false, nextRepayment: '01/06/2026', fund: 'Johnson Retirement Fund' },
];

interface Valuation {
  asset: string;
  type: 'property' | 'shares' | 'unlisted' | 'collectables';
  lastValuationDate: string;
  value: number;
  nextValuationDue: string;
  status: 'current' | 'due-soon' | 'overdue';
  fund: string;
}

const VALUATIONS: Valuation[] = [
  { asset: '42 Collins St, Melbourne', type: 'property', lastValuationDate: '15/09/2025', value: 730_000, nextValuationDue: '15/09/2026', status: 'current', fund: 'Smith Family Super Fund' },
  { asset: 'BHP Group Ltd (500 shares)', type: 'shares', lastValuationDate: '31/03/2026', value: 22_500, nextValuationDue: '30/06/2026', status: 'current', fund: 'Smith Family Super Fund' },
  { asset: '15 Surf Parade, Gold Coast', type: 'property', lastValuationDate: '01/03/2024', value: 800_000, nextValuationDue: '01/03/2025', status: 'overdue', fund: 'Johnson Retirement Fund' },
  { asset: 'Johnson Trading Pty Ltd (20%)', type: 'unlisted', lastValuationDate: '30/06/2025', value: 150_000, nextValuationDue: '30/06/2026', status: 'due-soon', fund: 'Johnson Retirement Fund' },
  { asset: 'Australian Gold Coins Collection', type: 'collectables', lastValuationDate: '15/11/2024', value: 65_000, nextValuationDue: '15/11/2025', status: 'overdue', fund: 'Johnson Retirement Fund' },
];

const VAL_STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  current: { label: 'Current', variant: 'default' },
  'due-soon': { label: 'Due Soon', variant: 'secondary' },
  overdue: { label: 'Overdue', variant: 'destructive' },
};

interface Collectable {
  item: string;
  description: string;
  purchaseDate: string;
  cost: number;
  currentValue: number;
  storageLocation: string;
  insurancePolicy: string;
  storedCorrectly: boolean;
  usedByMembers: boolean;
}

const COLLECTABLES: Collectable[] = [
  { item: 'Gold Coins', description: 'Australian Gold Nugget 1oz x 20', purchaseDate: '10/06/2021', cost: 48_000, currentValue: 65_000, storageLocation: 'ANZ Safety Deposit Box #4421', insurancePolicy: 'Allianz #POL-889421', storedCorrectly: true, usedByMembers: false },
  { item: 'Wine Collection', description: 'Penfolds Grange 2010-2018 (24 bottles)', purchaseDate: '15/03/2022', cost: 18_000, currentValue: 28_500, storageLocation: 'Langton\'s Cellar #LNG-3318', insurancePolicy: 'QBE #POL-112847', storedCorrectly: true, usedByMembers: false },
  { item: 'Artwork', description: 'Sidney Nolan - Ned Kelly Series Print', purchaseDate: '22/08/2023', cost: 35_000, currentValue: 42_000, storageLocation: 'Member\'s residence', insurancePolicy: 'AAMI #POL-554231', storedCorrectly: false, usedByMembers: true },
];

interface NALIResult {
  transaction: string;
  type: string;
  parties: string;
  marketRate: string;
  actualRate: string;
  riskFlag: 'high' | 'medium' | 'low';
  fund: string;
}

const NALI_RESULTS: NALIResult[] = [
  { transaction: 'Lease - Gold Coast property', type: 'Rental income', parties: 'Johnson Family Trust', marketRate: '$850/wk', actualRate: '$500/wk', riskFlag: 'high', fund: 'Johnson Retirement Fund' },
  { transaction: 'Accounting fees', type: 'Expense', parties: 'Smith & Co', marketRate: '$5,000 p.a.', actualRate: '$4,500 p.a.', riskFlag: 'low', fund: 'Smith Family Super Fund' },
  { transaction: 'Loan interest - LRBA', type: 'Interest expense', parties: 'Johnson Family Trust', marketRate: '6.25%', actualRate: '4.50%', riskFlag: 'high', fund: 'Johnson Retirement Fund' },
  { transaction: 'Property management', type: 'Expense', parties: 'Johnson Property Mgmt', marketRate: '$8,000 p.a.', actualRate: '$12,000 p.a.', riskFlag: 'medium', fund: 'Johnson Retirement Fund' },
];

const RISK_BADGE: Record<string, { variant: 'default' | 'secondary' | 'destructive'; color: string }> = {
  high: { variant: 'destructive', color: 'text-red-600' },
  medium: { variant: 'secondary', color: 'text-amber-600' },
  low: { variant: 'default', color: 'text-green-600' },
};

// ==================== Component ====================

export default function SMSFInvestmentCompliancePage() {
  const [selectedFundId, setSelectedFundId] = useState<string>('');

  // Fetch funds list
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundId = selectedFundId || fundsData?.[0]?.id || '';

  // Fetch assets for selected fund
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['smsf-assets', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/assets`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch LRBA for selected fund
  const { data: lrbaData, isLoading: lrbaLoading } = useQuery({
    queryKey: ['smsf-lrba', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/lrba`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch compliance for selected fund
  const { data: complianceData, isLoading: complianceLoading } = useQuery({
    queryKey: ['smsf-compliance', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/compliance`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Use API data with mock fallback
  const funds = complianceData?.funds || FUNDS;
  const relatedPartyTxs = complianceData?.relatedPartyTransactions || RELATED_PARTY_TXS;
  const lrbas = lrbaData || LRBAS;
  const valuations = assetsData?.valuations || VALUATIONS;
  const collectables = assetsData?.collectables || COLLECTABLES;
  const naliResults = complianceData?.naliResults || NALI_RESULTS;

  const isLoading = fundsLoading || assetsLoading || lrbaLoading || complianceLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="Investment Compliance"
        description="Monitor sole purpose test, in-house assets, related parties and regulatory compliance"
      />

      {/* Fund Selector */}
      {fundsData && fundsData.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground">Fund:</label>
          <Select value={fundId} onValueChange={setSelectedFundId}>
            <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select a fund..." /></SelectTrigger>
            <SelectContent>
              {fundsData.map((f: any) => (
                <SelectItem key={f.id} value={f.id}>{f.fund_name || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      <Tabs defaultValue="sole-purpose" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="sole-purpose">Sole Purpose Test</TabsTrigger>
          <TabsTrigger value="in-house">In-House Assets</TabsTrigger>
          <TabsTrigger value="related-parties">Related Parties</TabsTrigger>
          <TabsTrigger value="lrba">LRBA</TabsTrigger>
          <TabsTrigger value="valuations">Valuations</TabsTrigger>
          <TabsTrigger value="collectables">Collectables</TabsTrigger>
          <TabsTrigger value="nali">NALI</TabsTrigger>
        </TabsList>

        {/* ===== Sole Purpose Test ===== */}
        <TabsContent value="sole-purpose">
          <div className="grid gap-4 md:grid-cols-2">
            {funds.map(fund => {
              const passed = fund.solePurposeChecks.filter(c => c.passed).length;
              const total = fund.solePurposeChecks.length;
              const allPassed = passed === total;
              return (
                <Card key={fund.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{fund.name}</CardTitle>
                      <div className={cn(
                        'h-8 w-8 rounded-full flex items-center justify-center',
                        allPassed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      )}>
                        {allPassed
                          ? <CheckCircle2 className="h-5 w-5" />
                          : <XCircle className="h-5 w-5" />
                        }
                      </div>
                    </div>
                    <CardDescription>{passed}/{total} criteria passed</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {fund.solePurposeChecks.map((check, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          {check.passed
                            ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                            : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                          }
                          <div>
                            <span className={cn(!check.passed && 'font-medium text-red-700')}>{check.criterion}</span>
                            {check.note && (
                              <p className="text-xs text-red-600 mt-0.5">{check.note}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ===== In-House Assets ===== */}
        <TabsContent value="in-house">
          <div className="grid gap-4 md:grid-cols-2">
            {funds.map(fund => {
              const ratio = (fund.inHouseAssetValue / fund.totalAssets) * 100;
              const breaching = ratio >= 5;
              const approaching = ratio >= 4 && ratio < 5;
              return (
                <Card key={fund.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{fund.name}</CardTitle>
                    <CardDescription>In-house asset limit: 5% of total assets</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(breaching || approaching) && (
                      <div className={cn(
                        'flex items-center gap-2 rounded-lg border p-3 text-sm',
                        breaching ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'
                      )}>
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        {breaching
                          ? 'BREACH: In-house assets exceed 5% limit. Rectification required.'
                          : 'WARNING: In-house assets approaching 5% threshold.'
                        }
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Total Assets</p>
                        <p className="font-bold text-lg">{fmtAUD(fund.totalAssets)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">In-House Assets</p>
                        <p className="font-bold text-lg">{fmtAUD(fund.inHouseAssetValue)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">IHA Ratio</p>
                        <p className={cn('font-bold text-lg', breaching ? 'text-red-600' : approaching ? 'text-amber-600' : 'text-green-600')}>
                          {fmtPct(ratio)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>0%</span>
                        <span className="font-medium">5% limit</span>
                      </div>
                      <div className="h-4 w-full rounded-full bg-secondary overflow-hidden relative">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            breaching ? 'bg-red-500' : approaching ? 'bg-amber-500' : 'bg-green-500'
                          )}
                          style={{ width: `${Math.min((ratio / 5) * 100, 100)}%` }}
                        />
                        <div className="absolute right-0 top-0 h-full w-0.5 bg-red-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ===== Related Parties ===== */}
        <TabsContent value="related-parties">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Flagged Related-Party Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Description</th>
                      <th className="pb-2 pr-4 font-medium">Parties</th>
                      <th className="pb-2 pr-4 font-medium text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium text-center">Risk</th>
                      <th className="pb-2 font-medium">Action Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatedPartyTxs.map((tx, i) => {
                      const risk = RISK_BADGE[tx.riskLevel];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 text-xs">{tx.date}</td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">{tx.fund}</td>
                          <td className="py-3 pr-4 font-medium">{tx.description}</td>
                          <td className="py-3 pr-4 text-xs">{tx.parties}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(tx.amount)}</td>
                          <td className="py-3 pr-4 text-center">
                            <Badge variant={risk.variant} className="capitalize">{tx.riskLevel}</Badge>
                          </td>
                          <td className="py-3 text-xs">{tx.actionRequired}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== LRBA ===== */}
        <TabsContent value="lrba">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Limited Recourse Borrowing Arrangements</CardTitle>
              <CardDescription>PCG 2016/5 safe harbour compliance for related party LRBAs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Property / Asset</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Lender</th>
                      <th className="pb-2 pr-4 font-medium text-right">Loan Balance</th>
                      <th className="pb-2 pr-4 font-medium text-right">Interest Rate</th>
                      <th className="pb-2 pr-4 font-medium text-right">LVR</th>
                      <th className="pb-2 pr-4 font-medium text-center">Safe Harbour</th>
                      <th className="pb-2 font-medium">Next Repayment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lrbas.map((l, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{l.asset}</td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">{l.fund}</td>
                        <td className="py-3 pr-4">{l.lender}</td>
                        <td className="py-3 pr-4 text-right">{fmtAUD(l.loanBalance)}</td>
                        <td className="py-3 pr-4 text-right">{l.interestRate.toFixed(2)}%</td>
                        <td className="py-3 pr-4 text-right">{l.lvr}%</td>
                        <td className="py-3 pr-4 text-center">
                          {l.safeHarbour
                            ? <Badge variant="default">Compliant</Badge>
                            : <Badge variant="destructive">Non-Compliant</Badge>
                          }
                        </td>
                        <td className="py-3 text-xs">{l.nextRepayment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Valuations ===== */}
        <TabsContent value="valuations">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Asset Valuations Register</CardTitle>
              <CardDescription>Track valuation currency across all fund assets</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {valuations.some(v => v.status === 'overdue') && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {valuations.filter(v => v.status === 'overdue').length} asset(s) have overdue valuations requiring immediate attention.
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Asset</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Last Valuation</th>
                      <th className="pb-2 pr-4 font-medium text-right">Value</th>
                      <th className="pb-2 pr-4 font-medium">Next Due</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {valuations.map((v, i) => {
                      const badge = VAL_STATUS_BADGE[v.status];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{v.asset}</td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">{v.fund}</td>
                          <td className="py-3 pr-4 capitalize"><Badge variant="outline">{v.type}</Badge></td>
                          <td className="py-3 pr-4 text-xs">{v.lastValuationDate}</td>
                          <td className="py-3 pr-4 text-right font-medium">{fmtAUD(v.value)}</td>
                          <td className="py-3 pr-4 text-xs">{v.nextValuationDue}</td>
                          <td className="py-3"><Badge variant={badge.variant}>{badge.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Collectables ===== */}
        <TabsContent value="collectables">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Collectables & Personal Use Assets Register</CardTitle>
              <CardDescription>Collectables must be stored securely and not used by members or related parties</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Item</th>
                      <th className="pb-2 pr-4 font-medium">Description</th>
                      <th className="pb-2 pr-4 font-medium">Purchase Date</th>
                      <th className="pb-2 pr-4 font-medium text-right">Cost</th>
                      <th className="pb-2 pr-4 font-medium text-right">Current Value</th>
                      <th className="pb-2 pr-4 font-medium">Storage</th>
                      <th className="pb-2 pr-4 font-medium">Insurance</th>
                      <th className="pb-2 font-medium">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collectables.map((c, i) => {
                      const compliant = c.storedCorrectly && !c.usedByMembers;
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{c.item}</td>
                          <td className="py-3 pr-4 text-xs">{c.description}</td>
                          <td className="py-3 pr-4 text-xs">{c.purchaseDate}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(c.cost)}</td>
                          <td className="py-3 pr-4 text-right font-medium">{fmtAUD(c.currentValue)}</td>
                          <td className="py-3 pr-4 text-xs">{c.storageLocation}</td>
                          <td className="py-3 pr-4 text-xs font-mono">{c.insurancePolicy}</td>
                          <td className="py-3">
                            {compliant
                              ? <Badge variant="default">Compliant</Badge>
                              : (
                                <div className="space-y-1">
                                  <Badge variant="destructive">Non-Compliant</Badge>
                                  <div className="text-xs text-red-600">
                                    {!c.storedCorrectly && <p>Storage breach</p>}
                                    {c.usedByMembers && <p>Used by member</p>}
                                  </div>
                                </div>
                              )
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== NALI ===== */}
        <TabsContent value="nali">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Non-Arm's Length Income (NALI) Scan Results</CardTitle>
              <CardDescription>Transactions compared to market rates for NALI risk assessment</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Transaction</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Parties</th>
                      <th className="pb-2 pr-4 font-medium">Market Rate</th>
                      <th className="pb-2 pr-4 font-medium">Actual Rate</th>
                      <th className="pb-2 font-medium text-center">NALI Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {naliResults.map((n, i) => {
                      const risk = RISK_BADGE[n.riskFlag];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{n.transaction}</td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">{n.fund}</td>
                          <td className="py-3 pr-4">{n.type}</td>
                          <td className="py-3 pr-4 text-xs">{n.parties}</td>
                          <td className="py-3 pr-4">{n.marketRate}</td>
                          <td className="py-3 pr-4">{n.actualRate}</td>
                          <td className="py-3 text-center">
                            <Badge variant={risk.variant} className="capitalize">{n.riskFlag}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
